import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonDialog } from '../../components/common';
import { Node, Edge, Viewport } from '@xyflow/react';
import { getTemplates, getTemplateCategories } from '../../utils/api';
import TemplateListView from './TemplateListView';
import TemplateDetailView from './TemplateDetailView';

// 模板数据结构定义
interface WorkflowTemplateConfig {
  nodes: Node[];
  edges: Edge[];
  viewport?: Viewport;
}

interface WorkflowTemplate {
  id: string;
  name: string;
  description: string;
  category: number;
  lang: string;
  type: string;
  status: number;
  is_system: boolean;
  config: WorkflowTemplateConfig;
  extras: Record<string, any>;
  created_at: string;
  updated_at: string;
}

interface WorkflowTemplatesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImportTemplate: (template: WorkflowTemplate) => void;
}

const WorkflowTemplatesDialog: React.FC<WorkflowTemplatesDialogProps> = ({
  isOpen,
  onClose,
  onImportTemplate
}) => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<number>(0);
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [categories, setCategories] = useState<Array<{ value: number; label: string }>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [categoriesLoading, setCategoriesLoading] = useState<boolean>(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WorkflowTemplate | null>(null);
  const [isDetailView, setIsDetailView] = useState<boolean>(false);

  // 加载模板分类列表
  useEffect(() => {
    console.log('模板分类useEffect触发，isOpen状态:', isOpen);
    // 只有当弹窗打开时才调用API
    if (!isOpen) {
      console.log('弹窗关闭，跳过分类获取');
      return;
    }

    const fetchCategories = async () => {
      console.log('开始执行fetchCategories函数');
      setCategoriesLoading(true);
      try {
        console.log('开始获取模板分类...');
        console.log('当前时间戳:', new Date().toISOString());
        const startTime = Date.now();
        const response = await getTemplateCategories();
        const endTime = Date.now();
        console.log(`分类API请求耗时: ${endTime - startTime}ms`);
        console.log('分类API返回完整响应:', response);
        console.log('分类API返回数据结构:', {
          data: response.data,
          hasCategories: response.data.categories !== undefined,
          categoriesLength: response.data.categories?.length,
          categoriesData: response.data.categories
        });
        if (response.data.categories && response.data.categories.length > 0) {
          console.log('使用后端返回的分类数据:', response.data.categories);
          // 确保数据格式正确，将后端返回的id映射到value，name映射到label
          const formattedCategories = response.data.categories.map((cat: any) => ({
            value: cat.id,
            label: cat.name
          }));
          console.log('格式化后的分类数据:', formattedCategories);
          console.log('更新categories状态前的当前状态:', categories);
          // 添加"All"分类到列表开头
          const categoriesWithAll = [
            { value: -1, label: 'All' }
          ].concat(formattedCategories);
          console.log('添加All分类后的完整分类数据:', categoriesWithAll);
          setCategories(categoriesWithAll);
          // 默认选择All分类
          console.log('默认选择All分类，value: -1');
          setSelectedCategory(-1);
          // 使用setTimeout来确保状态更新后再检查
          setTimeout(() => {
            console.log('分类更新后，当前分类状态:', categories);
          }, 100);
        } else {
          console.log('后端未返回分类数据或分类数据为空');
          // 后端未返回分类数据时，保持分类为空
          setCategories([]);
        }
      } catch (error) {
        console.error('获取模板分类失败:', error);
        // 发生错误时，保持分类为空
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
        console.log('分类加载完成，当前分类状态:', categories);
        console.log('categoriesLoading状态设置为false');
      }
    };

    fetchCategories();
  }, [isOpen]);

  // 加载模板列表，只有当弹窗打开、分类变化且有分类时才调用API
  useEffect(() => {
    console.log('模板列表useEffect触发，状态:', {
      isOpen,
      selectedCategory,
      categoriesLength: categories.length
    });
    // 只有当弹窗打开且有分类时才调用API
    if (!isOpen || categories.length === 0) {
      console.log('跳过模板获取，条件不满足');
      return;
    }

    const fetchTemplates = async () => {
      console.log('开始执行fetchTemplates函数，分类ID:', selectedCategory);
      setLoading(true);
      try {
        console.log('开始调用模板API...');
        console.log('当前时间戳:', new Date().toISOString());
        const startTime = Date.now();
        // 调用真实API获取模板列表，传入当前选择的分类进行过滤
        const response = await getTemplates(selectedCategory);
        const endTime = Date.now();
        console.log(`模板API请求耗时: ${endTime - startTime}ms`);
        console.log('模板API调用成功:', response);

        // 转换API返回的模板格式为WorkflowTemplate
        const apiTemplates = response.data.templates || [];
        console.log('获取到的模板数量:', apiTemplates.length);

        // 构建完整的模板数据，使用后端返回的实际节点和边配置
        const workflowTemplates: WorkflowTemplate[] = apiTemplates.map((apiTemplate: any) => {
          // 检查后端是否返回了节点配置
          if (!apiTemplate.config?.nodes || apiTemplate.config.nodes.length === 0) {
            throw new Error(`模板 ${apiTemplate.name} 缺少节点配置`);
          }

          if (!apiTemplate.config?.edges) {
            throw new Error(`模板 ${apiTemplate.name} 缺少边配置`);
          }

          console.log('处理模板数据:', {
            name: apiTemplate.name,
            category: apiTemplate.category,
            type: apiTemplate.type,
            nodesCount: apiTemplate.config.nodes.length,
            edgesCount: apiTemplate.config.edges.length
          });

          return {
            id: apiTemplate.id,
            name: apiTemplate.name,
            description: apiTemplate.description,
            category: apiTemplate.category || 0,
            lang: apiTemplate.lang || 'zh',
            type: apiTemplate.type,
            status: apiTemplate.status,
            is_system: apiTemplate.is_system || false,
            config: {
              nodes: apiTemplate.config.nodes,
              edges: apiTemplate.config.edges,
              viewport: apiTemplate.config.viewport
            },
            extras: apiTemplate.extras || {},
            created_at: apiTemplate.created_at || new Date().toISOString(),
            updated_at: apiTemplate.updated_at || new Date().toISOString()
          };
        });

        console.log('构建完成的模板数据数量:', workflowTemplates.length);
        // 直接使用API返回的模板，不再进行前端过滤
        setTemplates(workflowTemplates);
        console.log('模板数据状态已更新');
      } catch (error) {
        console.error('Failed to fetch templates:', error);
        console.error('获取模板列表失败:', error);
      } finally {
        setLoading(false);
        console.log('模板加载完成，loading状态设置为false');
      }
    };

    fetchTemplates();
  }, [selectedCategory, isOpen, categories.length]);



  return (
    <CommonDialog
      isOpen={isOpen}
      onClose={onClose}
      title={t('canvas.templates.dialog.title')}
      width={1000}
      height={700}
    >
      <div className="flex h-full overflow-hidden">
        {/* 左侧分类列表 */}
        <div className="w-56 bg-gray-800/60 border-r border-gray-700 overflow-y-auto">
          <div className="p-5">
            <h3 className="text-lg font-semibold mb-5 text-white">{t('canvas.templates.dialog.categories')}</h3>
            {categoriesLoading ? (
              <div className="flex justify-center items-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
              </div>
            ) : categories.length === 0 ? (
              <div className="flex justify-center items-center py-6 text-gray-400 text-sm">
                {t('canvas.templates.dialog.noCategories')}
              </div>
            ) : (
              <div className="space-y-2">
                {categories.map((category) => (
                  <button
                    key={category.value}
                    className={`w-full text-left px-4 py-3 rounded-lg transition-all text-sm ${selectedCategory === category.value
                      ? 'bg-gray-700 text-gray-200 font-medium'
                      : 'hover:bg-gray-700/80 text-gray-300'
                      }`}
                    onClick={() => {
                      setSelectedCategory(category.value);
                      // 如果当前在详情页，切换到列表页
                      if (isDetailView) {
                        setIsDetailView(false);
                      }
                    }}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 右侧模板预览区 */}
        <div className="flex-1 overflow-y-auto">

          {categories.length === 0 ? (
            <div className="flex justify-center items-center h-64 text-gray-400">
              {t('canvas.templates.dialog.noCategoryData')}
            </div>
          ) : loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : templates.length === 0 ? (
            <div className="flex justify-center items-center h-64 text-gray-400">
              {t('canvas.templates.dialog.noTemplates')}
            </div>
          ) : isDetailView && selectedTemplate ? (
            <TemplateDetailView
              template={selectedTemplate}
              categories={categories}
              onBack={() => setIsDetailView(false)}
              onImportTemplate={onImportTemplate}
              onClose={onClose}
            />
          ) : (
            <TemplateListView
              templates={templates}
              categories={categories}
              onPreviewTemplate={(template) => {
                setSelectedTemplate(template);
                setIsDetailView(true);
              }}
              onImportTemplate={onImportTemplate}
              onClose={onClose}
            />
          )}
        </div>
      </div>


    </CommonDialog>
  );
};

export default WorkflowTemplatesDialog;
export type { WorkflowTemplate };